import { defineStore } from 'pinia'
import * as Sentry from '@sentry/nuxt'
import { invoke } from '~/lib/tauriInvoke'
import type { Message } from '~/types/messages'
import type { GroupUser } from '~/types/groups'

// Message search in a group (the search bar in the chat header + the results panel). The search
// itself runs on the server — `search_messages` → Messages.SearchMessages → OpenSearch — over every
// channel of the group the user may read; the client only holds the current query, filters and the
// pages of results seen so far.

export type MessageSearchSort = 'relevance' | 'newest' | 'oldest'
export type MessageSearchHas = 'attachment' | 'image' | 'video' | 'file' | 'link'

export interface MessageSearchHit {
  message: Message
  /** Content fragments with matches wrapped in U+E000 … U+E001 (see lib/chat/searchHighlight.ts). */
  highlights: string[]
}

export interface MessageSearchFilters {
  /** «В:» — channel ids. */
  channelIds: string[]
  /** «От:» — the authors themselves, so their chips can show a name/avatar. */
  authors: GroupUser[]
  /** «Есть:» — all must hold. */
  has: MessageSearchHas[]
  /** «До» / «После» — local calendar dates, `YYYY-MM-DD`. */
  before: string | null
  after: string | null
}

export interface SearchMessagesResponse {
  hits: Array<{ message: Message | null, highlights: string[] }>
  total: number
  next_page_token: string
}

export const MESSAGE_SEARCH_PAGE_SIZE = 25

function emptyFilters(): MessageSearchFilters {
  return { channelIds: [], authors: [], has: [], before: null, after: null }
}

/** Start of a local calendar day as an ISO instant. */
function dayStartIso(day: string): string {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(y!, (m ?? 1) - 1, d ?? 1).toISOString()
}

let requestSeq = 0

export const useMessageSearchStore = defineStore('messageSearch', {
  state: () => ({
    /** A search has run and its results are current (cleared by `close`/`reset`). */
    isOpen: false,
    /** The desktop popup is shown — while the search field is focused, and after a search until
     * Escape / a click outside hides it (the results stay, and come back on the next focus). */
    popupVisible: false,
    groupId: null as string | null,
    /** What's in the search field. */
    query: '',
    /** The query the shown results are for (the field can be edited without searching yet). */
    submittedQuery: '',
    filters: emptyFilters(),
    sort: 'relevance' as MessageSearchSort,
    hits: [] as MessageSearchHit[],
    total: 0,
    nextPageToken: '',
    isLoading: false,
    isLoadingMore: false,
    error: null as string | null,
    /** Bumped to ask the header's search field to take focus (⌘F / Ctrl+F). */
    focusRequest: 0,
  }),

  getters: {
    hasFilters: (state) =>
      state.filters.channelIds.length > 0 || state.filters.authors.length > 0 || state.filters.has.length > 0
      || !!state.filters.before || !!state.filters.after,
    /** Something to search for — a query or at least one filter. */
    canSearch(state): boolean {
      return !!state.query.trim() || this.hasFilters
    },
    hasMore: (state) => !!state.nextPageToken,
  },

  actions: {
    /** Switching groups starts over: another group's query and filters mean nothing here. */
    useGroup(groupId: string | null) {
      if (this.groupId === groupId) return
      this.groupId = groupId
      this.close()
      this.query = ''
      this.submittedQuery = ''
      this.filters = emptyFilters()
      this.sort = 'relevance'
    },

    /** «Поиск в канале» (the shortcut): limit to this channel — a filter chip the user can drop. */
    scopeToChannel(channelId: string | null) {
      if (channelId) this.filters.channelIds = [channelId]
    },

    requestFocus() {
      this.focusRequest++
    },

    showPopup() {
      this.popupVisible = true
    },

    /** First Escape: hide the popup, keep the query and results. */
    hidePopup() {
      this.popupVisible = false
    },

    close() {
      this.popupVisible = false
      this.isOpen = false
      requestSeq++
      this.hits = []
      this.total = 0
      this.nextPageToken = ''
      this.isLoading = false
      this.isLoadingMore = false
      this.error = null
    },

    /** Clears query and filters and closes the panel. */
    reset() {
      this.query = ''
      this.submittedQuery = ''
      this.filters = emptyFilters()
      this.sort = 'relevance'
      this.close()
    },

    setSort(sort: MessageSearchSort) {
      if (this.sort === sort) return
      this.sort = sort
      if (this.isOpen) void this.search()
    },

    toggleChannel(channelId: string) {
      const ids = this.filters.channelIds
      this.filters.channelIds = ids.includes(channelId) ? ids.filter((id) => id !== channelId) : [...ids, channelId]
      void this.searchIfReady()
    },

    toggleAuthor(user: GroupUser) {
      const authors = this.filters.authors
      this.filters.authors = authors.some((a) => a.id === user.id)
        ? authors.filter((a) => a.id !== user.id)
        : [...authors, user]
      void this.searchIfReady()
    },

    toggleHas(has: MessageSearchHas) {
      const list = this.filters.has
      this.filters.has = list.includes(has) ? list.filter((h) => h !== has) : [...list, has]
      void this.searchIfReady()
    },

    setDate(which: 'before' | 'after', day: string | null) {
      this.filters[which] = day || null
      void this.searchIfReady()
    },

    /** Both bounds at once (the date picker's range / presets) — one search, not two. */
    setDateRange(after: string | null, before: string | null) {
      this.filters.after = after || null
      this.filters.before = before || null
      void this.searchIfReady()
    },

    clearFilters() {
      this.filters = emptyFilters()
      void this.searchIfReady()
    },

    /** A filter changed: search again if there's still something to search for, else close. */
    async searchIfReady() {
      if (this.canSearch) await this.search()
      else this.close()
    },

    request(pageToken: string) {
      const f = this.filters
      return invoke<SearchMessagesResponse>('search_messages', {
        payload: {
          group_id: this.groupId,
          query: this.query.trim(),
          channel_ids: f.channelIds,
          author_ids: f.authors.map((a) => a.id),
          has: f.has,
          before: f.before ? dayStartIso(f.before) : null,
          after: f.after ? dayStartIso(f.after) : null,
          sort: this.sort,
          page_size: MESSAGE_SEARCH_PAGE_SIZE,
          page_token: pageToken || null,
        },
      })
    },

    /** Runs the search from the first page and opens the results panel. */
    async search() {
      if (!this.groupId || !this.canSearch) return
      const seq = ++requestSeq
      this.isOpen = true
      this.popupVisible = true
      this.submittedQuery = this.query.trim()
      this.isLoading = true
      this.isLoadingMore = false
      this.error = null
      this.hits = []
      this.total = 0
      this.nextPageToken = ''
      try {
        const res = await this.request('')
        if (seq !== requestSeq) return
        this.hits = toHits(res)
        this.total = res.total
        this.nextPageToken = res.next_page_token
      } catch (e: any) {
        if (seq !== requestSeq) return
        this.error = e?.message || String(e)
        Sentry.captureException(e, { extra: { action: 'search_messages', groupId: this.groupId } })
      } finally {
        if (seq === requestSeq) this.isLoading = false
      }
    },

    /** Next page (infinite scroll in the results panel). */
    async loadMore() {
      if (!this.nextPageToken || this.isLoading || this.isLoadingMore) return
      const seq = requestSeq
      const token = this.nextPageToken
      this.isLoadingMore = true
      try {
        const res = await this.request(token)
        if (seq !== requestSeq) return
        const seen = new Set(this.hits.map((h) => h.message.id))
        this.hits = [...this.hits, ...toHits(res).filter((h) => !seen.has(h.message.id))]
        this.total = Math.max(res.total, this.hits.length)
        this.nextPageToken = res.next_page_token
      } catch (e: any) {
        if (seq !== requestSeq) return
        this.error = e?.message || String(e)
      } finally {
        if (seq === requestSeq) this.isLoadingMore = false
      }
    },
  },
})

export function toHits(res: SearchMessagesResponse): MessageSearchHit[] {
  return res.hits
      .filter((h): h is { message: Message, highlights: string[] } => !!h.message)
      .map((h) => ({ message: h.message, highlights: h.highlights ?? [] }))
}
